import { apiFetch } from "./client";

// ─── Storage types (match backend response shapes) ───

type ObjectCounts = {
  readonly messages: number;
  readonly media: number;
  readonly artifacts: number;
  readonly knowledge: number;
};

export type StorageUser = {
  readonly user_id: string;
  readonly display_name: string;
  readonly storage_used_bytes: number;
  readonly storage_limit_bytes: number;
  readonly plan_type: string;
  readonly object_counts: ObjectCounts;
  readonly sync_status: string;
};

export type ObjectBreakdown = {
  readonly object_type: string;
  readonly count: number;
  readonly size_bytes: number;
};

export type SyncCursorInfo = {
  readonly device_id: string;
  readonly conversation_id: string;
  readonly last_seq: number;
  readonly synced_at: string;
};

export type StorageTraceRef = {
  readonly ref_type:
    | "message_archive"
    | "file_upload"
    | "cloud_preview_session"
    | "public_space_item";
  readonly ref_id: string;
  readonly conversation_id: string | null;
  readonly message_id: string | null;
  readonly upload_id: string | null;
};

export type RecentStorageChange = {
  readonly id: string;
  readonly object_type: string;
  readonly size_bytes: number;
  readonly status: string;
  readonly bucket_binding: string;
  readonly conversation_id: string | null;
  readonly created_at: string;
  readonly deleted_at: string | null;
  readonly trace_status: "resolved" | "unresolved";
  readonly trace_refs: ReadonlyArray<StorageTraceRef>;
};

export type StorageUserDetail = {
  readonly user_id: string;
  readonly display_name: string;
  readonly storage_used_bytes: number;
  readonly storage_limit_bytes: number;
  readonly plan_type: string;
  readonly breakdown: ReadonlyArray<ObjectBreakdown>;
  readonly sync_cursors: ReadonlyArray<SyncCursorInfo>;
  readonly recent_changes: ReadonlyArray<RecentStorageChange>;
};

type RecalculateResult = {
  readonly user_id: string;
  readonly previous_bytes: number;
  readonly recalculated_bytes: number;
};

type CleanupResult = {
  readonly user_id: string;
  readonly marked_count: number;
};

// ─── Summary Job types ───

export type SummaryJob = {
  readonly id: string;
  readonly user_id: string;
  readonly target_type: string;
  readonly target_id: string;
  readonly status: string;
  readonly model: string | null;
  readonly token_usage_in: number | null;
  readonly token_usage_out: number | null;
  readonly grant_id: string | null;
  readonly error_message: string | null;
  readonly created_at: string;
  readonly completed_at: string | null;
};

type GrantInfo = {
  readonly id: string;
  readonly scope_type: string;
  readonly scope_id: string;
  readonly expires_at: string;
  readonly created_at: string;
};

type ThreadInfo = {
  readonly id: string;
  readonly title_preview: string | null;
  readonly status: string;
  readonly created_at: string;
};

export type SummaryJobDetail = SummaryJob & {
  readonly grant: GrantInfo | null;
  readonly summary_thread: ThreadInfo | null;
};

// ─── Storage API ───

type StorageListParams = {
  readonly limit?: number;
  readonly offset?: number;
  readonly filter?: string;
};

type StorageListResponse = {
  readonly data: {
    readonly items: ReadonlyArray<StorageUser>;
    readonly total: number;
  };
};

export async function fetchStorageUsers(
  params: StorageListParams,
): Promise<StorageListResponse["data"]> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.offset) qs.set("offset", String(params.offset));
  if (params.filter) qs.set("filter", params.filter);

  const query = qs.toString();
  const path = `/admin/storage/users${query ? `?${query}` : ""}`;
  const res = await apiFetch<StorageListResponse>(path);
  return res.data;
}

export async function fetchStorageDetail(
  userId: string,
): Promise<StorageUserDetail> {
  const res = await apiFetch<{ data: StorageUserDetail }>(
    `/admin/storage/users/${userId}`,
  );
  return res.data;
}

export async function recalculateStorage(
  userId: string,
  reason: string,
): Promise<RecalculateResult> {
  const res = await apiFetch<{ data: RecalculateResult }>(
    `/admin/storage/users/${userId}/recalculate`,
    { method: "POST", body: JSON.stringify({ reason }) },
  );
  return res.data;
}

export async function runCleanup(
  userId: string,
  reason: string,
): Promise<CleanupResult> {
  const res = await apiFetch<{ data: CleanupResult }>(
    `/admin/storage/users/${userId}/run-cleanup`,
    { method: "POST", body: JSON.stringify({ reason }) },
  );
  return res.data;
}

// ─── Summary Jobs API ───

type SummaryListParams = {
  readonly limit?: number;
  readonly offset?: number;
  readonly status?: string;
  readonly user_id?: string;
};

type SummaryListResponse = {
  readonly data: {
    readonly items: ReadonlyArray<SummaryJob>;
    readonly total: number;
  };
};

export async function fetchSummaryJobs(
  params: SummaryListParams,
): Promise<SummaryListResponse["data"]> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.offset) qs.set("offset", String(params.offset));
  if (params.status) qs.set("status", params.status);
  if (params.user_id) qs.set("user_id", params.user_id);

  const query = qs.toString();
  const path = `/admin/summary-jobs${query ? `?${query}` : ""}`;
  const res = await apiFetch<SummaryListResponse>(path);
  return res.data;
}

export async function fetchSummaryJobDetail(
  id: string,
): Promise<SummaryJobDetail> {
  const res = await apiFetch<{ data: SummaryJobDetail }>(
    `/admin/summary-jobs/${id}`,
  );
  return res.data;
}

// Wave 1B-C T-1BC.5 removed `retrySummaryJob`: the backend
// `POST /admin/summary-jobs/:id/retry` endpoint is gone because summary
// generation runs inside the Hashee Agent deployment (`apps/agent-hashee/`)
// and the `SUMMARY_JOB_QUEUE` binding no longer exists. Historical jobs
// are still viewable via the list + detail endpoints above.
